import { IS_CLIENT, IS_PRODUCTION, sha256Hash } from '@repo/common-utils';
import { AirbridgeAPI } from 'airbridge-web-sdk-loader/build/airbridge_api';

import { AirbridgeInitializeProps, SendEventParams, SetUserParams, EventTrackingInitializeProps, SendEventReturn } from '../types';

import { BaseService } from './BaseService';

let airbridge: AirbridgeAPI | null = null;

export class AirbridgeService extends BaseService {
  private readonly airbridgeAppId?: string;
  private readonly airbridgeWebToken?: string;
  private readonly airbridgeConfig?: AirbridgeInitializeProps['airbridgeConfig'];
  private eventQueue: SendEventParams[] = [];

  constructor({ appId, clientId, airbridgeAppId, airbridgeWebToken, airbridgeConfig }: EventTrackingInitializeProps & AirbridgeInitializeProps) {
    super(appId, clientId);
    this.airbridgeAppId = airbridgeAppId;
    this.airbridgeWebToken = airbridgeWebToken;
    this.airbridgeConfig = airbridgeConfig;
    this.init();
  }

  protected async init() {
    if (!IS_CLIENT || !this.airbridgeAppId || !this.airbridgeWebToken) return;
    if (!airbridge) {
      airbridge = await import('airbridge-web-sdk-loader').then((module) => module.default);

      airbridge?.init(
        {
          app: this.airbridgeAppId,
          webToken: this.airbridgeWebToken,
          userHash: false,
          shareCookieSubdomain: true,
          utmParsing: true,
          ...this.airbridgeConfig
        },
        (error: unknown) => {
          if (error) {
            console.error('Airbridge init error', error);
          }
          Object.keys(this.commonAttributes).forEach((key) => {
            airbridge?.setUserAttribute(key, this.commonAttributes[key]);
          });
          this.eventQueue.forEach((params) => {
            this.sendEvent(params);
          });
          this.eventQueue = [];
        }
      );
    }
  }

  async setUser({ id = '', phone = '', email = '' }: SetUserParams) {
    try {
      if (this.airbridgeAppId && this.airbridgeWebToken) {
        airbridge?.setUserID(id);
        airbridge?.setUserEmail(await sha256Hash(email || ''));
        airbridge?.setUserPhone(await sha256Hash(phone || ''));
        airbridge?.events.send('airbridge.user.signin');
      }
    } catch (err) {
      this.printLog(err);
    }
  }

  sendEvent(params: SendEventParams): SendEventReturn {
    if (!airbridge?.isSDKEnabled) {
      this.eventQueue.push(params);
      return;
    }
    const eventNameParams = [params?.category, params?.object, params?.detail, params?.action];
    const name = eventNameParams.filter((value) => !!value).join('_');
    const isExistAmountAttribute = typeof params?.attributes?.amount !== 'undefined';
    const customAttributes = {
      ...this.commonAttributes,
      ...params?.attributes,
      currency: isExistAmountAttribute ? 'KRW' : undefined,
      is_server: false
    };

    airbridge?.events.send(name, {
      action: params?.action,
      value: (params?.attributes?.value ?? (isExistAmountAttribute ? Number(params.attributes?.amount ?? 0) : undefined)) as number | undefined,
      customAttributes
    });
    this.printLog(name, customAttributes);

    return {
      config: {
        app: this.airbridgeAppId,
        webToken: this.airbridgeWebToken,
        userHash: false,
        shareCookieSubdomain: true,
        ...this.airbridgeConfig
      },
      event: {
        name: name,
        action: params?.action,
        value: params.attributes?.value ?? (isExistAmountAttribute ? Number(params.attributes?.amount ?? 0) : undefined),
        customAttributes
      }
    };
  }

  async unsetUser(): Promise<void> {
    if (airbridge?.isSDKEnabled) {
      airbridge?.events.send('airbridge.user.signout');
      airbridge?.clearUser();
    }
  }

  private printLog(event: unknown, params?: Record<string, unknown>) {
    if (!IS_PRODUCTION && process.env.NEXT_PUBLIC_TRACKING_DEBUG_MODE === 'true') {
      console.info('%c[Airbridge]', 'font-weight: bold;', event, params);
    }
  }
}
